//
// # useHierarchyData Hook
//
// Watches the active editor and extracts the instance hierarchy from the content.
//
// - For schematics: reads instances from the editor's in-memory Schematic content.
// - For layouts: reads instances from the LayoutContent's layoutEntity.implEntity.
// - For symbols or empty state: returns an empty list.
//
// Each entry has an entity reference for top-level instances, allowing the panel
// to select/highlight them in the viewport. Child instances (from expanded nodes)
// are loaded lazily via the server.
//
// Re-computes whenever the active ident changes or content is reloaded.
//

import { useEffect, useState, useCallback, useRef } from "react";
import { logger } from "CoreUtils";
import type { LayoutContent } from "EditorCore";
import { Ident } from "GenAlphaModel";
import type { schema } from "GenAlphaSchema";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";
import { createRequestContext } from "../../contextHelpers";

// Minimal type for accessing layout instance data from LayoutContent.
// We use structural types here because the full layout entity types
// are internal to EditorCore and not exported.
interface LayoutInstanceData {
  name: string;
  id?: number;
  target: {
    kind: string;
    layout?: { ident?: schema.Ident };
    ident?: schema.Ident;
  };
}

interface LayoutImplEntity {
  instances: {
    values: () => IterableIterator<{
      data: LayoutInstanceData;
      entityKind: number;
    }>;
  };
}

interface LayoutEntityWithImpl {
  implEntity?: LayoutImplEntity;
}

// A single entry in the hierarchy tree.
export interface HierarchyEntry {
  // Instance name (display label)
  readonly name: string;
  // Instance ID (for matching with "Show in Hierarchy" reveal targets)
  readonly id: number;
  // Target Ident that this instance references (for lazy child loading)
  readonly targetIdent: schema.Ident | null;
  // Display name for the target (cell name)
  readonly targetDisplayName: string;
  // Reference to the entity in the active editor (only for top-level instances).
  // Used for selection/highlight operations. Null for lazy-loaded children.
  readonly entityRef: unknown;
}

// A child entry loaded from expanding a tree node.
export interface ChildEntry {
  readonly name: string;
  readonly id: number;
  readonly targetIdent: schema.Ident | null;
  readonly targetDisplayName: string;
}

// A search result from recursive hierarchy search.
export interface HierarchySearchResult {
  // The matching instance
  readonly name: string;
  readonly id: number;
  readonly targetIdent: schema.Ident | null;
  readonly targetDisplayName: string;
  // Entity reference (only for top-level instances in the active editor)
  readonly entityRef: unknown;
  // Entity reference of the top-level ancestor in the viewport.
  // For top-level instances this equals entityRef; for nested instances
  // it points to the root-level instance that contains this one,
  // allowing "Zoom To" to select the containing instance.
  readonly rootEntityRef: unknown;
  // Ancestry path from root to this instance (not including the instance itself)
  readonly path: ReadonlyArray<string>;
}

export interface HierarchyDataState {
  // Top-level instance entries for the active design
  readonly entries: ReadonlyArray<HierarchyEntry>;
  // Content kind of the active design (for display purposes)
  readonly contentKind: "schematic" | "layout" | "symbol" | "graphics" | null;
  // Load children for a given target ident (lazy expansion)
  readonly loadChildren: (
    targetIdent: schema.Ident
  ) => Promise<ReadonlyArray<ChildEntry>>;
  // Search the hierarchy recursively for matching instances (capped at maxResults).
  // Returns a flat list of matches with ancestry paths.
  readonly searchHierarchy: (
    query: string,
    maxResults: number,
    signal: AbortSignal
  ) => Promise<ReadonlyArray<HierarchySearchResult>>;
}

// `visible` reflects whether the hierarchy panel's tab is currently on screen.
// When false, the top-level rebuild is skipped so edits to the design don't do
// any hierarchy work while the panel is hidden behind another tab. The tree
// rebuilds from the live model as soon as the panel becomes visible again
// (`visible` is a dependency of the build effect).
export function useHierarchyData(visible = true): HierarchyDataState {
  const workspace = useWorkspace();
  const snapshot = useWorkspaceSnapshot();
  const activeIdent = snapshot.activeIdent;
  // hierarchyVersion bumps on every design edit (markDirty); it drives the
  // top-level rebuild below so the tree stays live as instances are added,
  // renamed, or deleted. contentVersion (design reloads / abstract-view toggles)
  // also invalidates the tree, so we react to both.
  const hierarchyVersion = snapshot.hierarchyVersion;
  const contentVersion = snapshot.contentVersion;

  const [entries, setEntries] = useState<ReadonlyArray<HierarchyEntry>>([]);
  const [contentKind, setContentKind] =
    useState<HierarchyDataState["contentKind"]>(null);

  // Cache for loaded children, keyed by stringified ident.
  // Cleared when contentVersion changes (stale entity references).
  const childrenCache = useRef(new Map<string, ReadonlyArray<ChildEntry>>());

  useEffect(() => {
    // Clear children cache when content changes. Keyed on both versions:
    // contentVersion (design reload) and hierarchyVersion (incremental edits)
    // can each stale the cached child entities. Clearing only invalidates the
    // cache — expanded rows keep their locally-held children and re-fetch lazily
    // on the next expand, so this is cheap.
    childrenCache.current.clear();
  }, [contentVersion, hierarchyVersion]);

  useEffect(() => {
    // Skip the rebuild while the panel is hidden behind another tab. The effect
    // still re-runs when `visible` flips back to true (it's a dependency), so
    // the tree catches up with any edits made while it was hidden.
    if (!visible) return;

    if (!activeIdent) {
      setEntries([]);
      setContentKind(null);
      return;
    }

    const editor = workspace.getActiveEditor();
    if (!editor) {
      setEntries([]);
      setContentKind(null);
      return;
    }

    const content = editor.content;
    const kind = content.contentKind;
    setContentKind(kind);

    if (kind === "schematic") {
      // Schematic content has a Set<Instance> of instances.
      // We use a structural cast since Schematic is not exported from EditorCore.
      const schematic = content as unknown as {
        instances: Set<{
          data: { name: string; id?: number; target: schema.Ident };
        }>;
      };
      const result: HierarchyEntry[] = [];
      for (const instance of schematic.instances) {
        const data = instance.data;
        result.push({
          name: data.name,
          id: data.id ?? 0,
          targetIdent: data.target,
          targetDisplayName: Ident.displayName(data.target),
          entityRef: instance,
        });
      }
      // Sort alphabetically by name for consistent display
      result.sort((a, b) => a.name.localeCompare(b.name));
      setEntries(result);
    } else if (kind === "layout") {
      const layoutContent = content as LayoutContent;
      const layoutEntity =
        layoutContent.layoutEntity as unknown as LayoutEntityWithImpl;
      const implEntity = layoutEntity.implEntity;
      if (!implEntity) {
        setEntries([]);
        return;
      }
      const result: HierarchyEntry[] = [];
      for (const instance of implEntity.instances.values()) {
        const data = instance.data;
        const target = data.target;
        // Extract ident from bound or unbound target
        let targetIdent: schema.Ident | null = null;
        let targetDisplayName = "";
        if (target.kind === "bound" && target.layout) {
          const layout = target.layout as { ident?: schema.Ident };
          if (layout.ident) {
            targetIdent = layout.ident;
            targetDisplayName = Ident.displayName(layout.ident);
          }
        } else if (target.kind === "unbound" && target.ident) {
          targetIdent = target.ident;
          targetDisplayName = Ident.displayName(target.ident);
        }
        result.push({
          name: data.name,
          id: data.id ?? 0,
          targetIdent,
          targetDisplayName,
          entityRef: instance,
        });
      }
      result.sort((a, b) => a.name.localeCompare(b.name));
      setEntries(result);
    } else {
      // Symbol or graphics — no instances
      setEntries([]);
    }
  }, [visible, activeIdent, contentVersion, hierarchyVersion, workspace]);

  const loadChildren = useCallback(
    async (targetIdent: schema.Ident): Promise<ReadonlyArray<ChildEntry>> => {
      const key = Ident.stringify(targetIdent);
      const cached = childrenCache.current.get(key);
      if (cached) return cached;

      const ctx = createRequestContext(workspace.userId);
      const result = await workspace.client.request(ctx, {
        kind: "open",
        data: { kind: "ident", ident: targetIdent },
      });

      if (result.err) {
        logger.error(
          `[useHierarchyData] Failed to load children for ${Ident.displayName(
            targetIdent
          )}: ${JSON.stringify(result.val)}`
        );
        const empty: ReadonlyArray<ChildEntry> = [];
        childrenCache.current.set(key, empty);
        return empty;
      }

      const content = result.val as {
        contentKind?: string;
        instances?: Array<{ name: string; id?: number; target?: schema.Ident }>;
      };
      const children: ChildEntry[] = [];

      if (content.contentKind === "schematic" && content.instances) {
        for (const inst of content.instances) {
          children.push({
            name: inst.name,
            id: inst.id ?? 0,
            targetIdent: inst.target ?? null,
            targetDisplayName: inst.target
              ? Ident.displayName(inst.target)
              : "",
          });
        }
      } else if (content.contentKind === "layout") {
        // Layout content has instances in impl.instances
        const layoutData = content as {
          impl?: {
            instances?: Array<{
              name: string;
              id?: number;
              target?: schema.Ident;
            }>;
          };
        };
        if (layoutData.impl?.instances) {
          for (const inst of layoutData.impl.instances) {
            children.push({
              name: inst.name,
              id: inst.id ?? 0,
              targetIdent: inst.target ?? null,
              targetDisplayName: inst.target
                ? Ident.displayName(inst.target)
                : "",
            });
          }
        }
      }

      children.sort((a, b) => a.name.localeCompare(b.name));
      const frozen = Object.freeze(children);
      childrenCache.current.set(key, frozen);
      return frozen;
    },
    [workspace]
  );

  // Recursive BFS search through the hierarchy.
  // Matches instance names and target display names against the query.
  // Stops after collecting maxResults matches or exhausting the tree.
  const searchHierarchy = useCallback(
    async (
      query: string,
      maxResults: number,
      signal: AbortSignal
    ): Promise<ReadonlyArray<HierarchySearchResult>> => {
      const lower = query.toLowerCase();
      const results: HierarchySearchResult[] = [];

      // BFS queue: each item carries the instance data, its ancestry path,
      // its own entityRef, and the root-level entityRef for "Zoom To".
      interface QueueItem {
        name: string;
        id: number;
        targetIdent: schema.Ident | null;
        targetDisplayName: string;
        entityRef: unknown;
        rootEntityRef: unknown;
        path: ReadonlyArray<string>;
      }

      const queue: QueueItem[] = entries.map((e) => ({
        name: e.name,
        id: e.id,
        targetIdent: e.targetIdent,
        targetDisplayName: e.targetDisplayName,
        entityRef: e.entityRef,
        rootEntityRef: e.entityRef, // Top-level: root is itself
        path: [],
      }));

      // Track visited target idents to avoid cycles in the hierarchy
      const visited = new Set<string>();

      while (queue.length > 0 && results.length < maxResults) {
        if (signal.aborted) return results;

        const item = queue.shift()!;

        // Check if this item matches the query
        if (
          item.name.toLowerCase().includes(lower) ||
          item.targetDisplayName.toLowerCase().includes(lower)
        ) {
          results.push({
            name: item.name,
            id: item.id,
            targetIdent: item.targetIdent,
            targetDisplayName: item.targetDisplayName,
            entityRef: item.entityRef,
            rootEntityRef: item.rootEntityRef,
            path: item.path,
          });
        }

        // If this item has a target, enqueue its children for further searching
        if (item.targetIdent && results.length < maxResults) {
          const key = Ident.stringify(item.targetIdent);
          if (!visited.has(key)) {
            visited.add(key);
            const children = await loadChildren(item.targetIdent);
            if (signal.aborted) return results;

            const childPath = [...item.path, item.name];
            for (const child of children) {
              queue.push({
                name: child.name,
                id: child.id,
                targetIdent: child.targetIdent,
                targetDisplayName: child.targetDisplayName,
                entityRef: null, // Children don't have viewport entity refs
                rootEntityRef: item.rootEntityRef, // Propagate root ancestor
                path: childPath,
              });
            }
          }
        }
      }

      return results;
    },
    [entries, loadChildren]
  );

  return { entries, contentKind, loadChildren, searchHierarchy };
}
